import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

interface AlphaLoaderProps {
  size?: "mini" | "small" | "medium" | "large" | "xlarge" | "xxlarge";
  className?: string;
}

const sizeClasses = {
  mini: "w-6 h-6",
  small: "w-8 h-8", 
  medium: "w-12 h-12",
  large: "w-16 h-16",
  xlarge: "w-32 h-32",
  xxlarge: "w-48 h-48"
};

const textSizeClasses = {
  mini: "text-xs",
  small: "text-sm",
  medium: "text-base", 
  large: "text-lg",
  xlarge: "text-xl",
  xxlarge: "text-2xl"
};

export const AlphaLoader = ({ 
  size = "medium", 
  className
}: AlphaLoaderProps) => {
  const texts = ["Loading…", "Hold tight,", "it's a heavy load!"];
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showText, setShowText] = useState(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const cycle = () => {
      if (showText) {
        // Show text for 2 seconds, then hide it
        timeout = setTimeout(() => {
          setShowText(false);
        }, 2000);
      } else {
        // Hide text for 2 seconds, then show next text
        timeout = setTimeout(() => {
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
          setShowText(true);
        }, 2000);
      }
    };

    cycle();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [showText, currentTextIndex, texts.length]);

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      {/* ALPHA Logo with Animation */}
      <div className={cn("relative", sizeClasses[size])}>
        {/* Professional ALPHA logo */}
        <img
          src="/lovable-uploads/1955e6d6-bedc-4c98-9648-0ed3ae78bb24.png"
          alt="ALPHA"
          className="w-full h-full object-contain alpha-logo-breathe"
        />
        
        {/* Glowing border effect with rounded edges */}
        <div className="absolute inset-0 rounded-3xl border-2 border-primary/30 alpha-glow-professional" />
      </div>
      
      {/* Sequential loading text */}
      <div className="mt-3 text-center h-8 flex items-center justify-center">
        {showText && (
          <div className={cn(
            "font-medium transition-opacity duration-300",
            textSizeClasses[size]
          )}>
            <span className="block">
              {texts[currentTextIndex]}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};